import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { Card } from "@/components/ui/card";
import { Shimmer, ChartShimmer } from "@/components/ui/shimmer";
import { useChartColors } from "@/hooks/useChartColors";
import type { WalletTransaction } from "@/utils/dashboard-api";

interface MoneyFlowChartProps {
  transactions: WalletTransaction[];
  loading: boolean;
}

export default function MoneyFlowChart({ transactions, loading }: MoneyFlowChartProps) {
  const colors = useChartColors();

  const buckets = useMemo(() => {
    const map = new Map<string, { credits: number; debits: number }>();

    for (const txn of transactions) {
      const date = txn.created_at.slice(0, 10);
      const entry = map.get(date) ?? { credits: 0, debits: 0 };
      if (txn.amount >= 0) {
        entry.credits += txn.amount;
      } else {
        entry.debits += Math.abs(txn.amount);
      }
      map.set(date, entry);
    }

    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, vals]) => {
        const d = new Date(date);
        return {
          label: `${d.getDate()}/${d.getMonth() + 1}`,
          credits: +(vals.credits / 100).toFixed(2),
          debits: +(vals.debits / 100).toFixed(2),
        };
      });
  }, [transactions]);

  return (
    <Shimmer loading={loading} fallback={<ChartShimmer height={320} />}>
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-base font-semibold">Money Flow</h3>
              <p className="text-[13px] text-muted-foreground mt-0.5">Daily credits vs debits</p>
            </div>
          </div>

          {buckets.length === 0 ? (
          <div className="flex items-center justify-center h-[320px]">
            <span className="text-sm text-muted-foreground">No transactions in this period</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={buckets} margin={{ top: 5, bottom: 5, left: 0, right: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  borderRadius: "8px",
                  border: "1px solid var(--border)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  fontSize: "13px",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "13px" }} />
              <Bar dataKey="credits" name="Credits" fill={colors.success} radius={[4, 4, 0, 0]} />
              <Bar dataKey="debits" name="Debits" fill={colors.error} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      </Card>
    </Shimmer>
  );
}
